<template>
  <v-card class="notification-list-card">
    <v-card-title class="d-flex justify-space-between align-center px-4 py-3">
      <span class="text-h6">Уведомления</span>
      <v-btn icon="mdi-close" variant="text" size="small" @click="$emit('close')" />
    </v-card-title>
    <v-divider />
    <v-card-text class="pa-4">
      <div v-if="isLoading && notifications.length === 0" class="text-center py-8">
        <v-progress-circular color="primary" :indeterminate="true" />
      </div>
      <div v-else-if="notifications.length === 0" class="py-2 px-0 text-grey">Нет уведомлений</div>
      <div v-else class="notification-list" @scroll="handleScroll">
        <div v-for="(item, index) in notifications" :key="item.id">
          <v-divider v-if="index > 0" class="my-3" />
          <v-row
            class="ma-0 px-0 flex-nowrap w-100 justify-space-between align-center notification-item-row"
            @click="() => handleNotificationClick(item)"
          >
            <v-col class="flex-grow-1 pa-0">
              <div
                v-if="item.message"
                :id="`text-notification-${index}`"
                class="text-body-2 line-height-2 mb-2"
              >
                {{ getShortTitle(item.message) }}
              </div>
              <v-tooltip
                :activator="`#text-notification-${index}`"
                location="right"
                :max-width="400"
              >
                {{ item.message }}
              </v-tooltip>
              <div class="text-grey text-caption">
                {{ formatDate(item.date) }}
              </div>
            </v-col>
            <v-col class="px-0 flex-grow-0 pa-0 ml-2">
              <v-btn
                :id="`notification-btn-${index}`"
                :color="item.new !== 'Y' ? 'grey' : 'primary'"
                icon="mdi-bell-outline"
                variant="text"
                class="user"
                @click.stop="() => handleMarkAsRead(item)"
              />
            </v-col>
          </v-row>
        </div>
        <div v-if="isLoadingMore" class="text-center py-4">
          <v-progress-circular color="primary" :indeterminate="true" size="24" />
        </div>
        <div v-if="hasMore && !isLoadingMore" class="text-center py-4">
          <v-btn color="primary" variant="text" @click="loadMore"> Загрузить ещё </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { api } from '@/api/Api'
import moment from 'moment'

moment.locale('ru')

const props = defineProps({
  onMarkAsRead: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits(['close', 'view'])

const notifications = ref([])
const isLoading = ref(false)
const isLoadingMore = ref(false)
const currentPage = ref(1)
const hasMore = ref(true)
const totalPages = ref(1)

async function loadNotifications(page = 1, append = false) {
  if (page === 1) {
    isLoading.value = true
  } else {
    isLoadingMore.value = true
  }

  try {
    const response = await api.notifications().getAllMessages(page)
    const data = response?.data || {}
    const items = data.items || []
    const meta = data._meta || {}

    if (append) {
      notifications.value = [...notifications.value, ...items]
    } else {
      notifications.value = items
    }

    currentPage.value = meta.currentPage || page
    totalPages.value = meta.pageCount || 1
    hasMore.value = currentPage.value < totalPages.value
  } catch (error) {
    console.error('Ошибка при загрузке уведомлений:', error)
  } finally {
    isLoading.value = false
    isLoadingMore.value = false
  }
}

function loadMore() {
  if (hasMore.value && !isLoadingMore.value) {
    loadNotifications(currentPage.value + 1, true)
  }
}

function handleScroll(event) {
  const element = event.target
  const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight

  if (scrollBottom < 100 && hasMore.value && !isLoadingMore.value) {
    loadMore()
  }
}

function getShortTitle(message) {
  if (!message) return ''
  const maxLength = 100
  if (message.length <= maxLength) return message
  return message.substring(0, maxLength) + '...'
}

function formatDate(date) {
  if (!date) return ''

  const dateStr = String(date).trim()
  if (!dateStr) return ''

  let dateMoment = moment(dateStr)

  if (!dateMoment.isValid()) {
    const formats = [
      'DD.MM.YYYY HH:mm:ss',
      'DD.MM.YYYY HH:mm',
      'DD.MM.YYYY',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD',
    ]
    dateMoment = moment(dateStr, formats, true)
  }

  if (!dateMoment.isValid()) {
    return dateStr
  }

  const now = moment()
  const diffInMinutes = now.diff(dateMoment, 'minutes')
  const diffInHours = now.diff(dateMoment, 'hours')

  if (diffInMinutes < 1) {
    return 'только что'
  } else if (diffInMinutes < 60) {
    return dateMoment.fromNow()
  } else if (diffInHours < 24 && now.date() === dateMoment.date()) {
    return dateMoment.format('HH:mm')
  } else {
    return dateMoment.format('DD.MM.YYYY, HH:mm')
  }
}

async function handleMarkAsRead(notification) {
  if (notification.new === 'Y' && props.onMarkAsRead) {
    try {
      await props.onMarkAsRead(notification.id)
      notification.new = 'N'
    } catch (error) {
      console.error('Ошибка при отметке уведомления как прочитанного:', error)
    }
  }
}

function handleNotificationClick(notification) {
  emit('view', {
    id: notification.id,
    message: notification.message,
    date: notification.date,
  })
}

onMounted(() => {
  loadNotifications(1)
})
</script>

<style scoped>
.notification-list-card {
  width: 500px;
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.notification-list {
  max-height: 500px;
  overflow-y: auto;
}

.notification-item-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-item-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
